import React, { useEffect, useState } from 'react';

import { projectService } from '../services/projectService';

interface ProjectVisibilityDropdownProps {
  projectId?: string | null;
  isPublic: boolean;
  disabled?: boolean;
  iconOnly?: boolean;
  onVisibilityChange?: (projectId: string | null, isPublic: boolean) => void;
}

const ProjectVisibilityIcon: React.FC<{ isPublic: boolean }> = ({ isPublic }) => (
  <svg
    className="project-visibility-dropdown__icon"
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {isPublic ? (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a16 16 0 0 1 0 18 16 16 0 0 1 0-18Z" />
      </>
    ) : (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
      </>
    )}
  </svg>
);

export const ProjectVisibilityDropdown: React.FC<ProjectVisibilityDropdownProps> = ({
  projectId, isPublic, disabled = false, iconOnly = false, onVisibilityChange,
}) => {
  const [value, setValue] = useState(isPublic);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(isPublic);
  }, [isPublic]);

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value === 'public';

    if (nextValue === value || saving) return;

    const previousValue = value;
    setValue(nextValue);

    if (projectId) {
      setSaving(true);
      try {
        const response = await projectService.toggleVisibility(projectId, nextValue);
        setValue(response.is_public);
        onVisibilityChange?.(projectId, response.is_public);
      } catch {
        setValue(previousValue);
      } finally {
        setSaving(false);
      }
    } else {
      onVisibilityChange?.(null, nextValue);
    }
  };

  return (
    <label
      className={`project-visibility-dropdown${iconOnly ? ' project-visibility-dropdown--icon-only' : ''}`}
      onClick={(event) => event.stopPropagation()}
      title={value ? 'Project is Public' : 'Project is Private'}
    >
      {iconOnly && <ProjectVisibilityIcon isPublic={value} />}
      <select
        aria-label={iconOnly ? `Project visibility: ${value ? 'Public' : 'Private'}` : 'Project visibility'}
        className="project-visibility-dropdown__select"
        disabled={disabled || saving}
        onChange={handleChange}
        value={value ? 'public' : 'private'}
      >
        <option value="public">Public</option>
        <option value="private">Private</option>
      </select>
    </label>
  );
};
